import { Injectable } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { Router } from '@angular/router';
import { LogService } from '../services/logging.service';
import { Actions, IAppState, IAppConfig } from '../../store';

import { BaseDao } from '../daos';
import { SiteURLUtils, SiteURL } from '../../common/site.url.utils';
import { IMessage } from '../interfaces/Messages/IMessage';
import { ReservationService } from '../services/reservation.service';
import { Reservation } from '../model/reservation/reservation.model';
import { RESERVATION_STATUS_CANCELED } from '../model/reservation/reservation-status.model';
import { MessageCategory } from '../model/message/message-category.model';
import { HotelService } from '../services/hotel.service';
import { AppAction, AppActionState } from '../../store/interfaces/IAppState';
import { IResDirectNumber } from '../interfaces/ResDirect/IResDirectNumber';
import { ApplicationRouteNames } from '../../common/model/application-route-names.model';
import { RouteNames } from '../enums';
import { CommunicationPreferences } from '../model/communication/communication.preferences.model';
import { map } from 'rxjs/operators';
import { getFutureHotelDecorator } from './utils';

@Injectable()
export class CancelReservationDelegate {

  private CLASS_NAME = 'CancelReservationDelegate';
  private CANCELED_RESERVATION = 'errors.cancelled.default.text';
  private _appConfig;
  constructor(
    private _ngRedux: NgRedux<IAppState>,
    private _dao: BaseDao,
    private _actions: Actions,
    private _logService: LogService,
    private _router: Router,
    private _reservationService: ReservationService,
    private _hotelService: HotelService
  ) {
    this._appConfig = _ngRedux.getState().appConfig as IAppConfig;
  }

  deleteReservation = (reservationId: string, lastName: string, communicationPreferences: CommunicationPreferences, useCache: boolean = false) => {
    return this._reservationService.deleteReservation(reservationId, lastName, communicationPreferences)
      .subscribe(
        cancellation => this.deleteReservationSuccess(cancellation, reservationId, lastName),
        error => {
          this._logService.error(`Error deleting reservation. Reservation: ${reservationId}`, this.CLASS_NAME, error);

          // set the reservation cancellationNumber to a bogus number and dispatch it; this will disable the cancel button
          const hotelReservation: Reservation = this._ngRedux.getState().reservation;
          const modifiedHotelReservation = JSON.parse(JSON.stringify(hotelReservation));
          modifiedHotelReservation.cancellationNumber = '99999';
          this._actions.dispatchReservationAction(AppAction.CANCEL_RESERVATION, AppActionState.ERROR, modifiedHotelReservation, error);
        },
        () => {
          this._logService.info('Successfully deleted reservation', this.CLASS_NAME, reservationId);
        }
      );
  }

  fetchReservation(reservationId: string, lastName: string, route: RouteNames, useCache: boolean = true) {
    this._actions.dispatchReservationAction(AppAction.GET_RESERVATION, AppActionState.INITIAL);
    this._reservationService.getReservation(reservationId, lastName, useCache)
      .subscribe((reservation: Reservation) => {
        this.fetchReservationSuccess(reservation, route, lastName, reservationId);
      },
        error => {
          this._logService.error(`Error retrieving all reservation information. Reservation: ${reservationId}`, this.CLASS_NAME, error);
          this._actions.dispatchReservationAction(AppAction.GET_RESERVATION, AppActionState.ERROR, null, error);
        },
        () => {
          this._logService.info('Retrieved all reservation information', this.CLASS_NAME, reservationId);
        });
  }

  getResDirect = (hotelId?: string, hotelBrandCode?: string, iata?: string, useCache: boolean = true) => {
    let url = this._appConfig.resDirectDAO.url;

    const {
      country,
      resDirectDAO: {
        apiKey,
        interval,
        retry,
        timeout
      }
    } = this._appConfig;

    if (hotelId && hotelBrandCode && country) {
      url += '?hotelMnemonic=' + hotelId + '&brandCode=' + hotelBrandCode + '&countryCode=' + country;
      if (iata) {
        url += '&iataNumber=' + iata;
      }
    } else {
      this._logService.error('Cannot retrieve Res Direct number without hotel code, brand code, and country', this.CLASS_NAME);
      this._actions.dispatchResDirect(AppAction.GET_RESDIRECT, <IResDirectNumber>{});
    }

    return this._dao.get(useCache, apiKey, interval, retry, timeout, url).subscribe((resDirect: IResDirectNumber) => {
      this._actions.dispatchResDirect(AppAction.GET_RESDIRECT, resDirect);
    },
      error => {
        this._logService.error(`Error retrieving reservation directory information. Hotel: ${hotelId}, Brand Code: ${hotelBrandCode}, IATA: ${iata}`, this.CLASS_NAME, error);
        this._actions.dispatchResDirect(AppAction.GET_RESDIRECT, <IResDirectNumber>{});
      },
      () => this._logService.info('Retrieved hotel reservation', this.CLASS_NAME, hotelId)
    );
  }

  deleteReservationSuccess = (cancellation, reservationId, lastName) => {
    if (cancellation.cancellationNumber && cancellation.cancellationNumber.length > 0) {
      // on success, dispatch the action and transfer to cancel confirmation view
      // tslint:disable-next-line:max-line-length
      this._actions.dispatchReservationAction(AppAction.CANCEL_RESERVATION, AppActionState.SUCCESS, Object.assign({}, this._ngRedux.getState().reservation, { cancellationNumber: cancellation.cancellationNumber, status: RESERVATION_STATUS_CANCELED }));
      this._actions.dispatchAddMessage(AppAction.ADD_MESSAGE, <IMessage>{ key: this.CANCELED_RESERVATION, messageCategory: MessageCategory.CANCEL_RESERVATION });
      const nextPage = `/cancellation-confirmation/${reservationId}/${lastName}`;
      this._router.navigateByUrl(nextPage);
    } else {
      // assume failure if there is no cancellation number
      this._logService.error(`Failed to receive cancellation number for deletion ${reservationId}`, this.CLASS_NAME);
      this._actions.dispatchReservationAction(AppAction.CANCEL_RESERVATION, AppActionState.ERROR);
      SiteURLUtils.redirectToSiteUrl(this._appConfig);
    }
  }

  fetchReservationSuccess = (reservation, route, lastName, reservationId) => {
    if (reservation.status === RESERVATION_STATUS_CANCELED && route === RouteNames.CancelReservation) {
      // the reservation has already been cancelled: go to the cancellation confirmation page
      const nextPage = `/${ApplicationRouteNames.CANCEL_CONFIRMATION}/${reservationId}/${lastName}`;
      this._router.navigateByUrl(nextPage);
    } else {
      if (reservation.guest.lastName.toUpperCase() !== lastName.toUpperCase()) {
        SiteURLUtils.redirectToSiteUrl(this._appConfig, SiteURL.HOMEPAGE_ERROR);
      } else {
        this._actions.dispatchReservationAction(AppAction.GET_RESERVATION, AppActionState.SUCCESS, reservation);
        this.fetchHotel(reservation.hotelMnemonic, RouteNames.CancelReservation);
      }
    }
  }

  fetchHotel(hotelMnemonic: string, route: RouteNames) {
    this._hotelService.getHotel(hotelMnemonic, true)
      .pipe(
        map(hotel => {
          const state = this._ngRedux.getState();
          const getRebrandedHotel = getFutureHotelDecorator(state);
          return getRebrandedHotel(hotel);
        })
      )
      .subscribe(hotel => {
        this._actions.dispatchHotelAction(AppAction.GET_HOTEL, AppActionState.SUCCESS, hotel);
        this.getResDirect(hotel.mnemonic, hotel.chainCode);
      },
        error => {
          this._logService.error(`Error retrieving hotel: ${hotelMnemonic}`, this.CLASS_NAME, error);
          this._actions.dispatchHotelAction(AppAction.GET_HOTEL, AppActionState.ERROR, null, error);
        }
      );
  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/delegates/cancel-reservation.delegate.ts